/**
 * Instance Repository
 * 
 * Handles workflow instance operations with GraphQL backend
 */

import { BaseRepository } from './BaseRepository';

export interface WorkflowInstance {
    id: string;
    workflowDefinitionId: string;
    name: string;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'paused' | 'cancelled';
    currentState: string;
    context: Record<string, any>;
    startedAt: string;
    completedAt?: string;
    assignedTo?: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    metadata?: Record<string, any>;
}

export interface StateTransition {
    id: string;
    instanceId: string;
    fromState: string;
    toState: string;
    event: string;
    timestamp: string;
    duration: number;
    success: boolean;
    error?: string;
    metadata?: Record<string, any>;
}

export interface InstanceMetrics {
    totalInstances: number;
    runningInstances: number;
    completedInstances: number;
    failedInstances: number;
    avgCompletionTime: number;
    successRate: number;
}

class InstanceRepository extends BaseRepository<WorkflowInstance> {
    protected entityName = 'Instance';

    // Find all instances with optional filtering
    async findAll(options?: {
        status?: string;
        assignedTo?: string;
        workflowDefinitionId?: string;
        limit?: number;
        offset?: number;
    }): Promise<WorkflowInstance[]> {
        const query = `
            query ListInstances(
                $status: InstanceStatus
                $assignedTo: String
                $workflowDefinitionId: String
                $limit: Int
                $offset: Int
                $tenant: String!
            ) {
                instances(
                    status: $status
                    assignedTo: $assignedTo
                    workflowDefinitionId: $workflowDefinitionId
                    limit: $limit
                    offset: $offset
                    tenant: $tenant
                ) {
                    id
                    workflowDefinitionId
                    name
                    status
                    currentState
                    context
                    startedAt
                    completedAt
                    assignedTo
                    priority
                    metadata
                }
            }
        `;

        const variables = {
            status: options?.status,
            assignedTo: options?.assignedTo,
            workflowDefinitionId: options?.workflowDefinitionId,
            limit: options?.limit,
            offset: options?.offset
        };
        const result = await this.query<{ instances: WorkflowInstance[] }>(query, variables);
        return result.instances || [];
    }

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<WorkflowInstance | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'WorkflowInstance',
            criticalityLevel: 'MEDIUM',
            metadata: { id, instanceRepository: true }
        });

        const query = `
            query GetInstanceByTenantAndId($id: String!, $tenant: String!) {
                instance(id: $id, tenant: $tenant) {
                    id
                    workflowDefinitionId
                    name
                    status
                    currentState
                    context
                    startedAt
                    completedAt
                    assignedTo
                    priority
                    metadata
                }
            }
        `;

        try {
            const result = await this.query<{ instance: WorkflowInstance | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 2 * 60 * 1000 // 2 minutes cache for instances
            });

            const instance = result.instance || null;

            // ✅ FERP SAFETY: Log successful operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'WorkflowInstance',
                    entityId: id,
                    found: !!instance,
                    timestamp: Date.now()
                });
            }

            return instance;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'WorkflowInstance',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Find instance by ID with full details (legacy method - redirects to findByTenantAndId)
    async findById(instanceId: string): Promise<WorkflowInstance | null> {
        return this.findByTenantAndId(this.tenant, instanceId);
    }

    // Get state transitions for an instance
    async getStateTransitions(instanceId: string): Promise<StateTransition[]> {
        const query = `
            query GetStateTransitions($instanceId: String!, $tenant: String!) {
                stateTransitions(instanceId: $instanceId, tenant: $tenant) {
                    id
                    instanceId
                    fromState
                    toState
                    event
                    timestamp
                    duration
                    success
                    error
                    metadata
                }
            }
        `;

        const result = await this.query<{ stateTransitions: StateTransition[] }>(query, { instanceId });
        return result.stateTransitions || [];
    }

    // Create new instance
    async create(instanceData: {
        workflowDefinitionId: string;
        name: string;
        context?: Record<string, any>;
        assignedTo?: string;
        priority?: 'low' | 'normal' | 'high' | 'urgent';
    }): Promise<WorkflowInstance> {
        const mutation = `
            mutation CreateInstance($input: CreateInstanceInput!, $tenant: String!) {
                createInstance(input: $input, tenant: $tenant) {
                    id
                    workflowDefinitionId
                    name
                    status
                    currentState
                    context
                    startedAt
                    assignedTo
                    priority
                    metadata
                }
            }
        `;

        const result = await this.mutation<{ createInstance: WorkflowInstance }>(mutation, { input: instanceData });
        return result.createInstance;
    }

    // Update instance
    async update(instanceId: string, updateData: Partial<WorkflowInstance>): Promise<WorkflowInstance> {
        const mutation = `
            mutation UpdateInstance($id: String!, $input: UpdateInstanceInput!, $tenant: String!) {
                updateInstance(id: $id, input: $input, tenant: $tenant) {
                    id
                    workflowDefinitionId
                    name
                    status
                    currentState
                    context
                    startedAt
                    completedAt
                    assignedTo
                    priority
                    metadata
                }
            }
        `;

    const result = await this.mutation<{ updateInstance: WorkflowInstance }>(mutation, {
        id: instanceId,
        input: updateData
    });
    return result.updateInstance;
  }

  // Start instance
  async start(instanceId: string): Promise<WorkflowInstance> {
        const mutation = `
            mutation StartInstance($id: String!, $tenant: String!) {
                startInstance(id: $id, tenant: $tenant) {
                    id
                    status
                    currentState
                    startedAt
                }
            }
        `;

    const result = await this.mutation<{ startInstance: WorkflowInstance }>(mutation, { id: instanceId });
    return result.startInstance;
  }

    async sendSignal(params: {
        instanceId: string;
        signalName: string;
        signalData: Record<string, unknown>;
        userId: string;
        sessionId: string;
        tenantId: string;
    }): Promise<{ success: boolean; instanceId: string; newState?: string; error?: string | null }> {
        const mutation = `
            mutation SendWorkflowSignal(
                $instanceId: String!
                $signalName: String!
                $signalData: JSON!
                $userId: String!
                $sessionId: String!
                $tenant: String!
            ) {
                sendWorkflowSignal(
                    instanceId: $instanceId
                    signalName: $signalName
                    signalData: $signalData
                    userId: $userId
                    sessionId: $sessionId
                    tenant: $tenant
                ) {
                    success
                    instanceId
                    newState
                    error
                }
            }
        `;

        const result = await this.mutation<{ sendWorkflowSignal: { success: boolean; instanceId: string; newState?: string; error?: string | null } }>(
            mutation,
            {
                instanceId: params.instanceId,
                signalName: params.signalName,
                signalData: params.signalData,
                userId: params.userId,
                sessionId: params.sessionId,
                tenant: params.tenantId
            }
        );

        return result.sendWorkflowSignal;
    }

    // Pause instance
    async pause(instanceId: string): Promise<WorkflowInstance> {
        const mutation = `
            mutation PauseInstance($id: String!, $tenant: String!) {
                pauseInstance(id: $id, tenant: $tenant) {
                    id
                    status
                    currentState
                }
            }
        `;

        const result = await this.mutation<{ pauseInstance: WorkflowInstance }>(mutation, { id: instanceId });
        return result.pauseInstance;
    }

    // Resume instance
    async resume(instanceId: string): Promise<WorkflowInstance> {
        const mutation = `
            mutation ResumeInstance($id: String!, $tenant: String!) {
                resumeInstance(id: $id, tenant: $tenant) {
                    id
                    status
                    currentState
                }
            }
        `;

        const result = await this.mutation<{ resumeInstance: WorkflowInstance }>(mutation, { id: instanceId });
        return result.resumeInstance;
    }

    // Cancel instance
    async cancel(instanceId: string, reason?: string): Promise<WorkflowInstance> {
        const mutation = `
            mutation CancelInstance($id: String!, $reason: String, $tenant: String!) {
                cancelInstance(id: $id, reason: $reason, tenant: $tenant) {
                    id
                    status
                    currentState
                    completedAt
                }
            }
        `;

        const result = await this.mutation<{ cancelInstance: WorkflowInstance }>(mutation, {
            id: instanceId,
            reason
        });
        return result.cancelInstance;
    }

    // Delete instance
    async delete(instanceId: string): Promise<boolean> {
        const mutation = `
            mutation DeleteInstance($id: String!, $tenant: String!) {
                deleteInstance(id: $id, tenant: $tenant)
            }
        `;

        const result = await this.mutation<{ deleteInstance: boolean }>(mutation, { id: instanceId });
        return result.deleteInstance || false;
    }

    // Get instance metrics
    async getMetrics(options?: {
        workflowDefinitionId?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<InstanceMetrics> {
        const query = `
            query GetInstanceMetrics(
                $workflowDefinitionId: String
                $startDate: String
                $endDate: String
                $tenant: String!
            ) {
                instanceMetrics(
                    workflowDefinitionId: $workflowDefinitionId
                    startDate: $startDate
                    endDate: $endDate
                    tenant: $tenant
                ) {
                    totalInstances
                    runningInstances
                    completedInstances
                    failedInstances
                    avgCompletionTime
                    successRate
                }
            }
        `;

        const result = await this.query<{ instanceMetrics: InstanceMetrics }>(query, options);
        return result.instanceMetrics || {
            totalInstances: 0,
            runningInstances: 0,
            completedInstances: 0,
            failedInstances: 0,
            avgCompletionTime: 0,
            successRate: 0
        };
    }
}

// Export singleton instance
export const instanceRepository = new InstanceRepository();
export { InstanceRepository };
